/**
 * Editable "raw status token -> system state" mapping table (CU-86cb1npz2,
 * gap #3): lets the operator map an arbitrary raw text token found in the
 * file's status/period cells (e.g. a supplier-specific word like "rwnj") to
 * one of the system's two states, FREE or UNAVAILABLE. A richer third state
 * for "reserved by someone else" (distinct from "sold") is explicitly
 * deferred — the DB's `availability_windows.state` check constraint only
 * allows `FREE`/`UNAVAILABLE` today, and this table matches that ceiling
 * rather than getting ahead of it.
 *
 * Same "Table primitives, per-row Select, unmapped rows highlighted" idiom
 * as `column-mapping-table.tsx` — an editable mapping table, not a read-only
 * preview grid, so it stays on `@ooh/ui/components/table` rather than
 * `DataTable`.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { SelectField, type SelectOption } from '@ooh/ui/components/select';
import { Badge } from '@ooh/ui/components/badge';
import { cn } from '@ooh/ui/lib/utils';

export type StatusTokenMappedState = 'FREE' | 'UNAVAILABLE' | '';

export interface StatusTokenMappingRow {
  id: string;
  /** Raw text exactly as found in the file's status/period cells. */
  rawToken: string;
  /** How many cells in the file carry this exact token. */
  occurrenceCount: number;
  /** The supplier's own explanation of the token, when the file provides one
   * (e.g. an in-sheet legend) — shown verbatim, never interpreted. */
  supplierLegendText?: string;
  /** '' when not yet mapped — every unmapped row today is silently dropped
   * at import time (no period is written for that cell) with no distinct
   * report entry, so this table's whole purpose is closing that gap before
   * confirm, not just after the fact. */
  mappedState: StatusTokenMappedState;
  onChange: (next: StatusTokenMappedState) => void;
}

export interface StatusTokenMappingTableLabels {
  heading: string;
  helperText: string;
  tokenHeader: string;
  occurrenceHeader: string;
  stateHeader: string;
  freeLabel: string;
  unavailableLabel: string;
  statePlaceholder: string;
  unmappedBadge: string;
}

export interface StatusTokenMappingTableProps {
  rows: StatusTokenMappingRow[];
  labels: StatusTokenMappingTableLabels;
  testIdPrefix?: string;
}

export function StatusTokenMappingTable({
  rows,
  labels,
  testIdPrefix = 'status-token-mapping',
}: StatusTokenMappingTableProps) {
  const stateOptions: SelectOption[] = [
    { value: 'FREE', label: labels.freeLabel },
    { value: 'UNAVAILABLE', label: labels.unavailableLabel },
  ];

  return (
    <div className="space-y-1.5" data-testid={testIdPrefix}>
      <p className="text-[13px] font-semibold text-foreground">{labels.heading}</p>
      <p className="text-xs text-muted-foreground">{labels.helperText}</p>
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{labels.tokenHeader}</TableHead>
              <TableHead>{labels.occurrenceHeader}</TableHead>
              <TableHead>{labels.stateHeader}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isUnmapped = row.mappedState === '';
              return (
                <TableRow
                  key={row.id}
                  listRow
                  className={cn(isUnmapped && 'bg-amber-50/50')}
                  data-testid={`${testIdPrefix}-row-${row.id}`}
                >
                  <TableCell className="px-4 py-2 align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs">{row.rawToken}</span>
                      {isUnmapped ? (
                        <Badge
                          variant="warning"
                          className="px-1.5 py-0 text-2xs font-normal whitespace-nowrap"
                        >
                          {labels.unmappedBadge}
                        </Badge>
                      ) : null}
                    </div>
                    {row.supplierLegendText ? (
                      <p className="mt-0.5 text-2xs italic text-muted-foreground">
                        „{row.supplierLegendText}”
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="px-4 py-2 align-top text-xs text-muted-foreground tabular-nums">
                    {row.occurrenceCount}
                  </TableCell>
                  <TableCell className="px-4 py-2 align-top">
                    <SelectField
                      options={stateOptions}
                      value={row.mappedState}
                      onChange={(next) => row.onChange(next as StatusTokenMappedState)}
                      placeholder={labels.statePlaceholder}
                      error={isUnmapped}
                      data-testid={`${testIdPrefix}-${row.id}-select`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
